import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser, LoginRequest } from '@mailflow/shared';
import { api, ApiRequestError } from './api.js';

/**
 * Der angemeldete Benutzer ist eine ganz normale Query. Es gibt keinen
 * separaten Auth-Zustand im Frontend, der mit dem Server auseinanderlaufen
 * koennte — die Sitzung liegt im Cookie, die Wahrheit beim Server.
 */
export const meQuery = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: async ({ signal }): Promise<CurrentUser | null> => {
    try {
      const data = await api.get<{ user: CurrentUser }>('/api/auth/me', signal);
      return data.user;
    } catch (err) {
      // 401 ist kein Fehlerfall, sondern die Antwort "nicht angemeldet".
      if (err instanceof ApiRequestError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 60_000,
  retry: false,
});

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: LoginRequest) =>
      api.post<{ user: CurrentUser }>('/api/auth/login', credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(meQuery.queryKey, data.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/auth/logout'),
    onSettled: () => {
      // Auch bei Fehler lokal abmelden: der Nutzer hat Abmelden gedrueckt,
      // die Oberflaeche darf nicht angemeldet aussehen.
      queryClient.setQueryData(meQuery.queryKey, null);
      void queryClient.invalidateQueries();
    },
  });
}

export interface ProjectItem {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export const projectsQuery = queryOptions({
  queryKey: ['projects'],
  queryFn: ({ signal }) => api.get<{ items: ProjectItem[] }>('/api/projects', signal),
  staleTime: 60_000,
});

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; domain?: string }) =>
      api.post<{ project: { id: string; name: string; domain?: string } }>('/api/projects', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

